import { CartLinesUpdateEvent } from '@shopify/events';

const visible = (el) => !!el && getComputedStyle(el).display !== 'none' &&
  getComputedStyle(el).visibility !== 'hidden' && el.getClientRects().length > 0;

const labelOf = (field) => (field.querySelector('.wcpa_field_label')?.textContent || '')
  .replace('*', '').trim();

const clearError = (field) => {
  field.classList.remove('rw-aco-error');
  field.querySelector('.rw-aco-error-message')?.remove();
};

const showError = (field, message) => {
  clearError(field);
  field.classList.add('rw-aco-error');
  const error = document.createElement('div');
  error.className = 'rw-aco-error-message';
  error.setAttribute('role', 'alert');
  error.textContent = message;
  field.appendChild(error);
};

function validate(form) {
  const wrapper = form.querySelector('.wcpa_form_outer, .wcpa_wrap');
  if (!wrapper) return null;

  for (const field of wrapper.querySelectorAll('.wcpa_field_wrap')) {
    if (!visible(field) || !field.querySelector('.wcpa_required_ast')) continue;
    clearError(field);
    const label = labelOf(field) || 'this option';

    const radios = [...field.querySelectorAll('input[type="radio"]:not(:disabled)')]
      .filter((input) => visible(input.closest('label, .wcpa_color') || input));
    if (radios.length && !radios.some((input) => input.checked)) {
      showError(field, `Please select ${label}.`);
      return field;
    }

    const checks = [...field.querySelectorAll('input[type="checkbox"]:not(:disabled)')]
      .filter((input) => visible(input.closest('label, .wcpa_checkbox') || input));
    if (checks.length && !checks.some((input) => input.checked)) {
      showError(field, `Please select ${label}.`);
      return field;
    }

    const control = field.querySelector('select, textarea, input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]):not([type="file"])');
    if (control && visible(control) && !String(control.value || '').trim()) {
      showError(field, `Please enter ${label}.`);
      return field;
    }
  }
  return null;
}

function syncProperties(form) {
  const wrapper = form.querySelector('.wcpa_form_outer, .wcpa_wrap');
  if (!wrapper) return;

  for (const field of wrapper.querySelectorAll('.wcpa_field_wrap')) {
    if (!visible(field)) continue;
    const label = labelOf(field);
    if (!label) continue;
    const generatedName = `properties[${label}]`;

    const canonicalHidden = field.id
      ? form.querySelector(`#${CSS.escape(field.id)}_hidden[name^="properties["]`)
      : null;
    const localHidden = field.querySelector('input[type="hidden"][name^="properties["]');

    const selected = field.querySelector('input[type="radio"]:checked');
    if (selected) {
      let hidden = canonicalHidden || localHidden;
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = generatedName;
        field.appendChild(hidden);
      }

      if (canonicalHidden && localHidden && localHidden !== canonicalHidden) {
        localHidden.remove();
      }

      hidden.value = selected.value;
      continue;
    }

    const checked = [...field.querySelectorAll('input[type="checkbox"]:checked')];
    if (checked.length) {
      let hidden = canonicalHidden || localHidden;
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = generatedName;
        field.appendChild(hidden);
      }

      if (canonicalHidden && localHidden && localHidden !== canonicalHidden) {
        localHidden.remove();
      }

      hidden.value = checked.map((input) => input.value).join(', ');
      continue;
    }

    const control = field.querySelector('select, textarea, input[type="text"], input[type="email"], input[type="number"], input[type="tel"], input[type="url"]');
    if (control) control.name = generatedName;
  }
}

function variantId(form) {
  const scope = form.closest('product-form-component')?.closest('.shopify-section, main') || document;
  const picker = scope.querySelector('variant-picker');
  const script = picker?.querySelector('script[type="application/json"]');
  if (picker && script) {
    try {
      const data = JSON.parse(script.textContent);
      const selected = [...picker.querySelectorAll('select[name^="options["]')].map((select) => select.value);
      const variant = data.variants?.find((item) =>
        item.options?.length === selected.length &&
        item.options.every((value, index) => String(value) === String(selected[index]))
      );
      if (variant?.id) return String(variant.id);
    } catch (error) {
      console.warn('[Aco cart bridge] Variant resolution failed.', error);
    }
  }
  return form.querySelector('input[name="id"]')?.value || '';
}

async function add(form, button) {
  const invalid = validate(form);
  if (invalid) {
    invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    invalid.querySelector('input, select, textarea')?.focus({ preventScroll: true });
    return;
  }

  syncProperties(form);
  const id = variantId(form);
  if (!id) {
    alert('Please select the product options again.');
    return;
  }

  let idInput = form.querySelector('input[name="id"]');
  if (!idInput) {
    idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    form.appendChild(idInput);
  }
  idInput.value = id;

  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((component) => component.dataset.sectionId).filter(Boolean);
  const data = new FormData(form);
  data.set('id', id);
  data.set('quantity', data.get('quantity') || '1');
  if (sectionIds.length) {
    data.delete('sections');
    data.append('sections', [...new Set(sectionIds)].join(','));
    data.append('sections_url', location.pathname);
  }

  let resolveCart, rejectCart;
  const promise = new Promise((resolve, reject) => {
    resolveCart = resolve;
    rejectCart = reject;
  });

  form.closest('product-form-component')?.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: id, quantity: Number(data.get('quantity')) || 1 }],
    promise,
  }));

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data,
      credentials: 'same-origin',
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed.');

    const cart = await fetch(Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());

    resolveCart({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'aco-horizon-cart-bridge',
        sourceId: form.id,
        itemCount: Number(data.get('quantity')) || 1,
        productId: form.closest('product-form-component')?.dataset.productId,
        sections: result.sections || {},
        didError: false,
      },
    });
  } catch (error) {
    rejectCart(error);
    alert(error.message || 'Unable to add this product to the cart.');
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('change', (event) => {
  const field = event.target.closest?.('.wcpa_field_wrap');
  if (field) clearError(field);
}, true);

window.addEventListener('click', (event) => {
  const button = event.target.closest?.('button[name="add"], .product-form__submit, [data-add-to-cart]');
  const form = button?.closest?.('form[action*="/cart/add"]');
  if (!button || !form || !form.querySelector('.wcpa_form_outer, .wcpa_wrap')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  add(form, button);
}, true);
